import React from "react";
import { Search, X } from "lucide-react";

export interface MobileSearchFieldProps {
  value: string;
  onChange: (value: string) => void;
  /** What the empty field reads as — "Search clients", not "Search". On a
   *  phone the field is a line of its own with no heading beside it to say
   *  what it searches. */
  placeholder: string;
  /**
   * What the field is called out loud, where that is not the placeholder.
   *
   * Only for a screen that already calls it something else out loud; the
   * placeholder is the name otherwise, so the two cannot drift apart.
   */
  label?: string;
}

/**
 * The search field a phone expects: a line of its own at the full width of the
 * screen, and a filled capsule rather than an outlined box.
 *
 * The fill is what says "type here" at a glance, where a hairline border reads
 * as one more edge among the cards below it. It fills white and takes a brand
 * edge while it is being typed into, so the field with focus is the one that
 * looks live.
 *
 * Shared rather than written per screen. Three of the details below are not
 * style choices and are exactly the ones a second copy quietly loses: the
 * 16px text, the keyboard hints, and the clear button. See each.
 */
const MobileSearchField: React.FC<MobileSearchFieldProps> = ({
  value,
  onChange,
  placeholder,
  label,
}) => (
  <div className="relative">
    <Search
      aria-hidden="true"
      className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-subtle"
    />
    <input
      type="search"
      value={value}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder}
      aria-label={label ?? placeholder}
      // What the phone's own keyboard makes of it: a search key rather than a
      // return, and none of the corrections a name would be mangled by.
      enterKeyHint="search"
      autoComplete="off"
      autoCorrect="off"
      spellCheck={false}
      // 16px is not a size choice: iOS zooms the page into any field it can
      // read at less, and the way back out is a pinch. The webkit clear button
      // is turned off because the one below is drawn where a thumb can reach
      // it.
      className="h-11 w-full rounded-full border border-transparent bg-[#F1F4F6] pl-11 pr-11 text-[16px] text-ink transition-colors placeholder:text-ink-subtle focus:border-brand focus:bg-white focus:outline-none [&::-webkit-search-cancel-button]:appearance-none"
    />
    {/* Only while there is something to clear, and a target of its own rather
        than a glyph: emptying the field is how the whole list comes back, and
        on a phone that should be one press rather than a held backspace. */}
    {value && (
      <button
        type="button"
        onClick={() => onChange("")}
        aria-label="Clear search"
        className="absolute right-1.5 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full text-ink-subtle"
      >
        <X aria-hidden="true" className="h-4 w-4" />
      </button>
    )}
  </div>
);

export default MobileSearchField;
